import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Wallet, CheckCircle, Clock, IndianRupee, Users, CalendarPlus, Edit, Trash2, Calculator } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SalaryPage = () => {
  const { isAdmin } = useAuth();
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [bulkBonus, setBulkBonus] = useState('0');
  const [bulkDeductions, setBulkDeductions] = useState('0');
  const [attendanceData, setAttendanceData] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    employee_name: '',
    month: MONTHS[new Date().getMonth()],
    year: new Date().getFullYear(),
    base_salary: '',
    bonus: '0',
    deductions: '0',
    working_days: 0,
    present_days: 0
  });

  useEffect(() => {
    fetchData();
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      const [salaryRes, employeesRes] = await Promise.all([
        axios.get(`${API}/salary-records`, {
          params: { month: selectedMonth, year: selectedYear }
        }),
        axios.get(`${API}/employees`)
      ]);
      setSalaryRecords(salaryRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      setFormData(prev => ({
        ...prev,
        employee_id: employee.id,
        employee_name: employee.name,
        base_salary: employee.salary?.toString() || '0'
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/salary-records`, {
        ...formData,
        base_salary: parseFloat(formData.base_salary),
        bonus: parseFloat(formData.bonus),
        deductions: parseFloat(formData.deductions)
      });
      toast.success('Salary record created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating salary record');
    }
  };

  const handlePaySalary = async (recordId) => {
    try {
      await axios.put(`${API}/salary-records/${recordId}/pay`);
      toast.success('Salary marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Error updating salary status');
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setFormData({
      employee_id: record.employee_id,
      employee_name: record.employee_name,
      month: record.month,
      year: record.year,
      base_salary: record.base_salary?.toString() || '0',
      bonus: record.bonus?.toString() || '0',
      deductions: record.deductions?.toString() || '0',
      working_days: record.working_days || 0,
      present_days: record.present_days || 0
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateRecord = async () => {
    try {
      await axios.put(`${API}/salary-records/${editingRecord.id}`, {
        base_salary: parseFloat(formData.base_salary),
        bonus: parseFloat(formData.bonus),
        deductions: parseFloat(formData.deductions),
        working_days: parseInt(formData.working_days) || 0,
        present_days: parseInt(formData.present_days) || 0
      });
      toast.success('Salary record updated successfully');
      setIsEditDialogOpen(false);
      setEditingRecord(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating salary record');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this salary record?')) {
      return;
    }
    try {
      await axios.delete(`${API}/salary-records/${recordId}`);
      toast.success('Salary record deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting salary record');
    }
  };

  const calculateFromAttendance = async (employeeId) => {
    try {
      const response = await axios.get(`${API}/salary-records/calculate/${employeeId}`, {
        params: { month: formData.month || selectedMonth, year: formData.year || selectedYear }
      });
      setAttendanceData(response.data);
      setFormData(prev => ({
        ...prev,
        employee_id: response.data.employee_id,
        employee_name: response.data.employee_name,
        base_salary: response.data.calculated_salary.toString(),
        working_days: response.data.working_days,
        present_days: response.data.payable_days
      }));
      toast.success(`Calculated: ${response.data.payable_days} days (${response.data.present_days} present + ${response.data.earned_leave_days} earned leave)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error calculating salary from attendance');
    }
  };

  const handleGenerateAllSalaries = async () => {
    setGeneratingAll(true);
    try {
      // Get employees who don't have salary record for selected month/year
      const existingEmployeeIds = salaryRecords.map(r => r.employee_id);
      const employeesWithoutSalary = employees.filter(e => !existingEmployeeIds.includes(e.id));
      
      if (employeesWithoutSalary.length === 0) {
        toast.warning('All employees already have salary records for this month');
        setGeneratingAll(false);
        setIsBulkDialogOpen(false);
        return;
      }

      // Create salary records for all employees based on attendance
      let successCount = 0;
      let failCount = 0;

      for (const emp of employeesWithoutSalary) {
        try {
          // First calculate salary from attendance
          const calcResponse = await axios.get(`${API}/salary-records/calculate/${emp.id}`, {
            params: { month: selectedMonth, year: selectedYear }
          });
          const calcData = calcResponse.data;
          
          // If no attendance recorded, use employee's base salary
          // Otherwise use calculated salary based on present days
          let baseSalary = emp.salary || 0;
          if (calcData.payable_days > 0) {
            baseSalary = calcData.calculated_salary;
          }
          
          await axios.post(`${API}/salary-records`, {
            employee_id: emp.id,
            employee_name: emp.name,
            month: selectedMonth,
            year: selectedYear,
            base_salary: baseSalary,
            working_days: calcData.working_days || 0,
            present_days: calcData.payable_days || 0,
            bonus: parseFloat(bulkBonus) || 0,
            deductions: parseFloat(bulkDeductions) || 0
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to create salary for ${emp.name}:`, err);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} salary records created based on attendance!`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} records failed to create`);
      }

      setIsBulkDialogOpen(false);
      setBulkBonus('0');
      setBulkDeductions('0');
      fetchData();
    } catch (error) {
      toast.error('Error generating salaries');
    } finally {
      setGeneratingAll(false);
    }
  };

  const handlePayAllPending = async () => {
    const pendingRecords = salaryRecords.filter(r => r.status === 'pending');
    if (pendingRecords.length === 0) {
      toast.warning('No pending salaries to pay');
      return;
    }

    try {
      let successCount = 0;
      for (const record of pendingRecords) {
        try {
          await axios.put(`${API}/salary-records/${record.id}/pay`);
          successCount++;
        } catch (err) {
          console.error(`Failed to pay salary for ${record.employee_name}`);
        }
      }
      toast.success(`${successCount} salaries marked as paid!`);
      fetchData();
    } catch (error) {
      toast.error('Error paying salaries');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      employee_name: '',
      month: selectedMonth,
      year: selectedYear,
      base_salary: '',
      bonus: '0',
      deductions: '0',
      working_days: 0,
      present_days: 0
    });
    setAttendanceData(null);
  };

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  const calculateNetSalary = () => {
    const base = parseFloat(formData.base_salary) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    return base + bonus - deductions;
  };

  // Stats
  const totalPaid = salaryRecords.filter(r => r.status === 'paid').reduce((sum, r) => sum + r.net_salary, 0);
  const totalPending = salaryRecords.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.net_salary, 0);
  const pendingCount = salaryRecords.filter(r => r.status === 'pending').length;
  const employeesWithoutSalary = employees.filter(e => !salaryRecords.map(r => r.employee_id).includes(e.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div data-testid="salary-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Salary Management</h1>
          <p className="text-slate-500 mt-1">Manage employee salaries and payments</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setIsBulkDialogOpen(true)}
              data-testid="generate-all-salary-btn"
              variant="outline"
              className="border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-navy"
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Generate Monthly Salary
            </Button>
            <Button
              onClick={() => { resetForm(); setIsDialogOpen(true); }}
              data-testid="create-salary-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90 shadow-lg"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Individual
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-navy/10 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-brand-navy" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Records</p>
                <h3 className="text-2xl font-bold text-brand-navy">{salaryRecords.length}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Paid</p>
                <h3 className="text-2xl font-bold text-emerald-600">{formatCurrency(totalPaid)}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <h3 className="text-2xl font-bold text-amber-600">{formatCurrency(totalPending)}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Remaining</p>
                <h3 className="text-2xl font-bold text-blue-600">{employeesWithoutSalary.length} Employees</h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-4">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[150px]" data-testid="filter-month">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(month => (
                    <SelectItem key={month} value={month}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-[120px]" data-testid="filter-year">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && pendingCount > 0 && (
              <Button
                onClick={handlePayAllPending}
                data-testid="pay-all-btn"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <IndianRupee className="h-4 w-4 mr-2" />
                Pay All Pending ({pendingCount})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-brand-navy">
            Salary Records - {selectedMonth} {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Employee</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center">Working Days</TableHead>
                <TableHead className="font-semibold text-slate-600 text-center">Present Days</TableHead>
                <TableHead className="font-semibold text-slate-600">Base Salary</TableHead>
                <TableHead className="font-semibold text-slate-600">Bonus</TableHead>
                <TableHead className="font-semibold text-slate-600">Deductions</TableHead>
                <TableHead className="font-semibold text-slate-600">Net Salary</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
                {isAdmin && <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {salaryRecords.map((record) => (
                <TableRow key={record.id} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium">{record.employee_name}</TableCell>
                  <TableCell className="text-center">
                    <span className="text-slate-600">{record.working_days || '-'}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="font-semibold text-blue-600">{record.present_days || '-'}</span>
                  </TableCell>
                  <TableCell>{formatCurrency(record.base_salary)}</TableCell>
                  <TableCell className="text-emerald-600">+{formatCurrency(record.bonus)}</TableCell>
                  <TableCell className="text-red-500">-{formatCurrency(record.deductions)}</TableCell>
                  <TableCell className="font-bold text-brand-navy">{formatCurrency(record.net_salary)}</TableCell>
                  <TableCell>
                    <Badge className={
                      record.status === 'paid'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }>
                      {record.status === 'paid' ? (
                        <><CheckCircle className="h-3 w-3 mr-1" /> Paid</>
                      ) : (
                        <><Clock className="h-3 w-3 mr-1" /> Pending</>
                      )}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditRecord(record)}
                          data-testid={`edit-salary-${record.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteRecord(record.id)}
                          data-testid={`delete-salary-${record.id}`}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        {record.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handlePaySalary(record.id)}
                            data-testid={`pay-salary-${record.id}`}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          >
                            <IndianRupee className="h-3 w-3 mr-1" />
                            Pay
                          </Button>
                        )}
                        {record.status === 'paid' && record.paid_date && (
                          <span className="text-xs text-slate-400">
                            Paid: {record.paid_date}
                          </span>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {salaryRecords.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                    No salary records found for this period
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Create Salary Record</DialogTitle>
            <DialogDescription>Generate a new salary record for an employee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Employee</Label>
              <Select onValueChange={handleEmployeeSelect}>
                <SelectTrigger data-testid="select-salary-employee">
                  <SelectValue placeholder="Choose an employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} - {formatCurrency(emp.salary)}/month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month</Label>
                <Select
                  value={formData.month}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, month: v }))}
                >
                  <SelectTrigger data-testid="select-salary-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(month => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select
                  value={formData.year.toString()}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, year: parseInt(v) }))}
                >
                  <SelectTrigger data-testid="select-salary-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Base Salary (₹)</Label>
                {formData.employee_id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => calculateFromAttendance(formData.employee_id)}
                    className="h-7 text-xs"
                  >
                    <Calculator className="h-3 w-3 mr-1" />
                    Calculate from Attendance
                  </Button>
                )}
              </div>
              <Input
                type="number"
                value={formData.base_salary}
                onChange={(e) => setFormData(prev => ({ ...prev, base_salary: e.target.value }))}
                data-testid="input-base-salary"
                placeholder="Enter base salary"
              />
              {attendanceData && (
                <p className="text-xs text-emerald-600">
                  {attendanceData.payable_days} days × ₹{attendanceData.per_day_salary}/day = ₹{attendanceData.calculated_salary}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bonus (₹)</Label>
                <Input
                  type="number"
                  value={formData.bonus}
                  onChange={(e) => setFormData(prev => ({ ...prev, bonus: e.target.value }))}
                  data-testid="input-bonus"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Deductions (₹)</Label>
                <Input
                  type="number"
                  value={formData.deductions}
                  onChange={(e) => setFormData(prev => ({ ...prev, deductions: e.target.value }))}
                  data-testid="input-deductions"
                  placeholder="0"
                />
              </div>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Net Salary:</span>
                <span className="text-2xl font-bold text-brand-navy">
                  {formatCurrency(calculateNetSalary())}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              data-testid="submit-salary-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!formData.employee_id || !formData.base_salary}
            >
              Create Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Generate Dialog */}
      <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy flex items-center gap-2">
              <CalendarPlus className="h-5 w-5" />
              Generate Monthly Salary for All Employees
            </DialogTitle>
            <DialogDescription>
              Create salary records for all employees who don't have one for {selectedMonth} {selectedYear}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Summary */}
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <Users className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-900">Summary</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-slate-600">Total Employees:</div>
                <div className="font-semibold">{employees.length}</div>
                <div className="text-slate-600">Already Generated:</div>
                <div className="font-semibold">{salaryRecords.length}</div>
                <div className="text-slate-600">To be Generated:</div>
                <div className="font-semibold text-blue-600">{employeesWithoutSalary.length}</div>
              </div>
            </div>

            {/* Employee list preview */}
            {employeesWithoutSalary.length > 0 && (
              <div className="border rounded-lg p-3 max-h-40 overflow-y-auto">
                <p className="text-xs text-slate-500 mb-2">Employees to generate salary for:</p>
                {employeesWithoutSalary.map(emp => (
                  <div key={emp.id} className="flex justify-between items-center py-1 text-sm">
                    <span>{emp.name}</span>
                    <span className="text-slate-500">{formatCurrency(emp.salary)}</span>
                  </div>
                ))}
              </div>
            )}

            {employeesWithoutSalary.length === 0 && (
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="text-emerald-700">All employees have salary records for this month!</p>
              </div>
            )}

            {/* Optional Bonus/Deductions */}
            {employeesWithoutSalary.length > 0 && (
              <>
                <div className="p-3 bg-blue-50 rounded-lg mb-4">
                  <p className="text-sm text-blue-700 font-medium">📊 Attendance Based Calculation:</p>
                  <p className="text-xs text-blue-600 mt-1">
                    Salary = (Employee Base Salary ÷ Working Days) × (Present Days + Earned Leave)
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Note: If no attendance recorded, full base salary will be used.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bonus for All (₹)</Label>
                    <Input
                      type="number"
                      value={bulkBonus}
                      onChange={(e) => setBulkBonus(e.target.value)}
                      data-testid="bulk-bonus"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Deductions for All (₹)</Label>
                    <Input
                      type="number"
                      value={bulkDeductions}
                      onChange={(e) => setBulkDeductions(e.target.value)}
                      data-testid="bulk-deductions"
                      placeholder="0"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDialogOpen(false)}>Cancel</Button>
            {employeesWithoutSalary.length > 0 && (
              <Button
                onClick={handleGenerateAllSalaries}
                data-testid="confirm-generate-all-btn"
                className="bg-brand-gold text-brand-navy hover:bg-brand-gold/90"
                disabled={generatingAll}
              >
                {generatingAll ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-brand-navy border-t-transparent rounded-full mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <CalendarPlus className="h-4 w-4 mr-2" />
                    Generate {employeesWithoutSalary.length} Records
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Salary Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Salary Record</DialogTitle>
            <DialogDescription>
              Update salary details for {formData.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Working Days</Label>
                <Input
                  type="number"
                  value={formData.working_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, working_days: e.target.value }))}
                  placeholder="Total working days"
                />
              </div>
              <div className="space-y-2">
                <Label>Present Days</Label>
                <Input
                  type="number"
                  value={formData.present_days}
                  onChange={(e) => setFormData(prev => ({ ...prev, present_days: e.target.value }))}
                  placeholder="Days present"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Base Salary (₹)</Label>
              <Input
                type="number"
                value={formData.base_salary}
                onChange={(e) => setFormData(prev => ({ ...prev, base_salary: e.target.value }))}
                placeholder="Enter base salary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bonus (₹)</Label>
                <Input
                  type="number"
                  value={formData.bonus}
                  onChange={(e) => setFormData(prev => ({ ...prev, bonus: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Deductions (₹)</Label>
                <Input
                  type="number"
                  value={formData.deductions}
                  onChange={(e) => setFormData(prev => ({ ...prev, deductions: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="text-sm text-slate-600">
                Net Salary: <span className="font-bold text-brand-navy">₹{calculateNetSalary().toLocaleString('en-IN')}</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsEditDialogOpen(false); setEditingRecord(null); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRecord} className="bg-brand-navy text-white hover:bg-brand-navy/90">
              Update Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalaryPage;
